import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Ruler, Clock, Factory } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import Layout from "../Layout";
import SEOHead from "../SEOHead";
import Breadcrumb from "../Breadcrumb";
import FAQSection from "../FAQSection";
import HeroSection from "./HeroSection";
import fabricationHero from "../assets/building.jpg"; // Adjust path based on your project structure
import { business } from "../../config/business";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const process = [
  { icon: Ruler, title: "Design", text: "Structural design engineered to your span, height and load requirements." },
  { icon: ShieldCheck, title: "Fabrication", text: "Steel cut, drilled and coated at our Hyderabad facility." },
  { icon: Factory, title: "Delivery", text: "Transport coordinated to your site across Telangana or AP." },
  { icon: Clock, title: "Erection", text: "On-site assembly through to a handover-ready structure." },
];

const faqs = [
  { q: "Where is Vasavi Structures based?", a: "We're based in Peerzadiguda, Hyderabad, Telangana — this is our design and fabrication facility, and our only physical location." },
  { q: "What areas do you serve?", a: "We deliver, transport and erect structures across Telangana and Andhra Pradesh from our Hyderabad facility." },
  { q: "Do you handle the full project or just fabrication?", a: "We typically manage design, fabrication, delivery and erection as one coordinated project." },
];

const About = () => {
  const services = [
    {
      title: "Industrial Sheds",
      description: "Built to handle heavy machinery, production lines, raw material storage, and 24/7 industrial activity.",
    },
    {
      title: "Fabrication Services",
      description: "From steel frames to complex structural elements — we fabricate with precision and reliability.",
    },
    {
      title: "Industrial Structures",
      description: "Large frameworks designed to support industrial operations with maximum safety and efficiency.",
    },
    {
      title: "Prefabricated Buildings",
      description: "Portable, quick-to-install, and cost-effective buildings for various commercial and industrial applications.",
    },
    {
      title: "Shed Fabrication Service",
      description: "Complete design-to-installation shed solutions tailored to your site requirements.",
    },
  ];

  const values = [
    {
      icon: "⭐",
      title: "Quality First",
      description: "We use premium-grade steel and certified materials for every project.",
    },
    {
      icon: "🤝",
      title: "Client-Centric Approach",
      description: "Your requirements drive our design, engineering, and execution.",
    },
    {
      icon: "⏱️",
      title: "On-Time Completion",
      description: "We respect project timelines and deliver as promised.",
    },
    {
      icon: "🛡️",
      title: "Durability & Safety",
      description: "Structures built to withstand time, usage, and weather conditions.",
    },
  ];

  return (
    <Layout>
      <SEOHead
        title="About Us | Vasavi Structures Hyderabad"
        description="Vasavi Structures is a trusted name in industrial and steel fabrication, delivering durable prefab buildings, sheds, roofing and custom structures."
        keywords="Vasavi Structures Hyderabad, industrial fabrication company, steel fabrication company, PEB manufacturer Hyderabad"
      />
      <Breadcrumb items={[{ name: "About Us" }]} />

      <main>
        {/* 1. Hero */}
        <HeroSection
          title="About Vasavi Structures"
          subtitle="Building the Future of Industrial Infrastructure"
          description="A Hyderabad-based steel fabrication and industrial construction company delivering high-performance structures that industries rely on every day."
          backgroundImage={fabricationHero}
          primaryButtonText="Get Quote"
          primaryButtonLink="/enquiry"
          secondaryButtonText="Contact Us"
          secondaryButtonLink="/contact"
        />

        {/* 2. Trust strip */}
        <section className="bg-primary py-6">
          <div className="container-custom">
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-primary-foreground text-center">
              <span className="font-montserrat font-semibold flex items-center gap-2"><Factory size={18} aria-hidden="true" /> Hyderabad-based facility</span>
              <span className="font-montserrat font-semibold flex items-center gap-2"><ShieldCheck size={18} aria-hidden="true" /> Design & fabrication under one roof</span>
              <span className="font-montserrat font-semibold flex items-center gap-2"><Clock size={18} aria-hidden="true" /> Telangana & AP delivery</span>
            </div>
          </div>
        </section>

        {/* 3. Our Story */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={fadeUp}>
                <h2 className="font-montserrat text-2xl md:text-4xl font-bold text-foreground mb-6">
                  Our <span className="text-primary">Story</span>
                </h2>
                <p className="text-muted-foreground mb-4 text-2xl md:text-base">
                  Founded with the goal of providing durable and affordable infrastructure for growing industries, Vasavi Structures has earned a reputation for quality craftsmanship and engineering excellence.
                </p>
                <p className="text-muted-foreground mb-4 text-1xl md:text-base">
                  Over the years, we have delivered structures for warehouses, factories, workshops, poultry farms, storage units, and manufacturing plants across Telangana.
                </p>
                <p className="text-muted-foreground text-1xl md:text-base">
                  Our approach combines modern fabrication technology, skilled workmanship, and smart engineering to produce structures that last for decades.
                </p>
              </motion.div>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                transition={{ staggerChildren: 0.08 }}
                className="grid grid-cols-2 gap-4"
              >
                {values.map((value) => (
                  <motion.div key={value.title} variants={fadeUp} className="p-6 bg-card rounded-xl shadow-lg card-hover">
                    <div className="text-4xl mb-3">{value.icon}</div>
                    <h3 className="font-montserrat font-bold text-foreground mb-2 text-1xl md:text-lg">
                      {value.title}
                    </h3>
                    <p className="text-1xl text-muted-foreground text-1xl md:text-sm">{value.description}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* 4. What We Do */}
        <section className="section-padding bg-muted">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="font-montserrat text-1xl md:text-4xl font-bold text-foreground mb-4">
                What We <span className="text-secondary">Do</span>
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto text-1xl md:text-base">
                Comprehensive industrial construction and fabrication services tailored to your needs.
              </p>
            </div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              transition={{ staggerChildren: 0.06 }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {services.map((service) => (
                <motion.div key={service.title} variants={fadeUp} className="p-6 bg-card rounded-xl shadow-lg card-hover">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <svg
                      className="w-6 h-6 text-primary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h3 className="font-montserrat text-1xl font-bold text-foreground mb-2 text-base md:text-lg">
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground text-1xl md:text-sm">{service.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* 5. Our process */}
        <section className="section-padding">
          <div className="container-custom">
            <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-foreground mb-10 text-center">
              How We Work
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {process.map((step, i) => (
                <div key={step.title} className="p-6 bg-card rounded-xl shadow-md text-center card-hover">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <step.icon className="text-primary" size={22} aria-hidden="true" />
                  </div>
                  <p className="text-xs font-montserrat font-bold text-secondary mb-2">STEP {i + 1}</p>
                  <h3 className="font-montserrat font-bold text-foreground mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 6. Trust Banner */}
        <section className="section-padding bg-secondary">
          <div className="container-custom text-center">
            <h2 className="font-montserrat text-2xl md:text-4xl font-bold text-secondary-foreground mb-4">
              Your Trusted Partner in Industrial Construction
            </h2>
            <p className="text-secondary-foreground/90 text-lg max-w-3xl mx-auto text-2xl md:text-base">
              At Vasavi Structures, we don't just build sheds — we build relationships built on trust, service, and long-term reliability.
            </p>
          </div>
        </section>

        {/* 7. FAQ */}
        <FAQSection faqs={faqs} />

        {/* 8. CTA */}
        <section className="section-padding bg-primary">
          <div className="container-custom text-center">
            <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-primary-foreground mb-4">
              Ready to Start Your Project?
            </h2>
            <p className="text-primary-foreground/90 mb-8 max-w-xl mx-auto">
              Tell us about your requirements and we'll get back with a design consultation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/enquiry" className="btn-secondary inline-block px-8 py-4">
                Get Project Estimate
              </Link>
              <a href={business.whatsapp.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-lg font-montserrat font-semibold hover:bg-[#20bd5a] transition">
                <FaWhatsapp size={20} aria-hidden="true" /> WhatsApp Us
              </a>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
};

export default About;
